"use client";

import { useEffect, useState } from "react";
import { XIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";

import { siteSwitchTarget } from "@/components/site/site-language-switcher";
import { PREFERRED_SITE_CHANGED_EVENT, readPreferredSite, rememberPreferredSite, savePreferredSite } from "@/lib/site/preferred-site-client";
import { apiFetch } from "@/lib/api-client";
import { suggestedLanguageSite, type LanguageSuggestion } from "@/lib/site/language-suggestion";
import { uiLanguageOf, type UiLanguage } from "@/lib/site/site-routing";
import type { SiteLanguageInfo } from "@/templates/site/types";

const DISMISSED_KEY = "cms_site_banner_dismissed";

const COPY: Record<
  UiLanguage,
  { preferred: (site: string) => string; available: (site: string) => string; go: (site: string) => string; dismiss: string }
> = {
  zh: {
    preferred: (site) => `你的常用站点是${site}`,
    available: (site) => `本站也有${site}版`,
    go: (site) => `查看${site}版`,
    dismiss: "关闭",
  },
  "zh-tw": {
    preferred: (site) => `你的常用網站是${site}`,
    available: (site) => `本站也有${site}版`,
    go: (site) => `查看${site}版`,
    dismiss: "關閉",
  },
  en: {
    preferred: (site) => `Your preferred site is ${site}`,
    available: (site) => `This site is also available in ${site}`,
    go: (site) => `View the ${site} version`,
    dismiss: "Dismiss",
  },
  ja: {
    preferred: (site) => `よく使うサイトは${site}です`,
    available: (site) => `${site}版もあります`,
    go: (site) => `${site}版を見る`,
    dismiss: "閉じる",
  },
  ko: {
    preferred: (site) => `자주 사용하는 사이트는 ${site}입니다`,
    available: (site) => `이 사이트는 ${site}(으)로도 제공됩니다`,
    go: (site) => `${site} 버전 보기`,
    dismiss: "닫기",
  },
  de: {
    preferred: (site) => `Ihre bevorzugte Website ist ${site}`,
    available: (site) => `Diese Website gibt es auch auf ${site}`,
    go: (site) => `Zur Version auf ${site}`,
    dismiss: "Schließen",
  },
  fr: {
    preferred: (site) => `Votre site préféré est ${site}`,
    available: (site) => `Ce site est aussi disponible en ${site}`,
    go: (site) => `Voir la version ${site}`,
    dismiss: "Fermer",
  },
  es: {
    preferred: (site) => `Su sitio preferido es ${site}`,
    available: (site) => `Este sitio también está disponible en ${site}`,
    go: (site) => `Ver la versión en ${site}`,
    dismiss: "Cerrar",
  },
  ru: {
    preferred: (site) => `Ваш основной сайт — ${site}`,
    available: (site) => `Этот сайт также доступен на языке: ${site}`,
    go: (site) => `Открыть версию: ${site}`,
    dismiss: "Закрыть",
  },
  pt: {
    preferred: (site) => `Seu site preferido é ${site}`,
    available: (site) => `Este site também está disponível em ${site}`,
    go: (site) => `Ver a versão em ${site}`,
    dismiss: "Fechar",
  },
};

export function SiteLanguageBanner({ language }: { language: SiteLanguageInfo }) {
  const t = useTranslations("site.common");
  const [suggestion, setSuggestion] = useState<LanguageSuggestion | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (language.sites.length < 2) return;
    let alive = true;
    let preferenceChanged = false;

    function updateSuggestion() {
      if (!alive) return;
      try {
        if (sessionStorage.getItem(DISMISSED_KEY) === "1") {
          setSuggestion(null);
          return;
        }
      } catch {}
      setSuggestion(suggestedLanguageSite(
        language,
        readPreferredSite(),
        navigator.languages?.length ? navigator.languages : [navigator.language],
      ));
    }

    function onPreferenceChange() {
      preferenceChanged = true;
      updateSuggestion();
    }
    window.addEventListener(PREFERRED_SITE_CHANGED_EVENT, onPreferenceChange);

    void apiFetch<{ member?: { preferredSite?: string | null } }>("/api/site/member-summary")
      .then((data) => {
        if (!alive || preferenceChanged) return;
        const code = data.member?.preferredSite;
        if (code && language.sites.some((site) => site.code === code)) {
          rememberPreferredSite(code);
        }
        updateSuggestion();
      })
      .catch(() => { if (!preferenceChanged) updateSuggestion(); });

    return () => {
      alive = false;
      window.removeEventListener(PREFERRED_SITE_CHANGED_EVENT, onPreferenceChange);
    };
  }, [language]);

  if (!suggestion) {
    return null;
  }

  const copy = COPY[uiLanguageOf(suggestion.site.locale)];
  const href = siteSwitchTarget(language, suggestion.site);
  const current = language.sites.find((site) => site.current);

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISSED_KEY, "1");
    } catch {}
    setSuggestion(null);
  };

  return (
    <div
      lang={suggestion.site.locale}
      role="status"
      className="fixed inset-x-0 top-[65px] z-30 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-border/40 bg-background/90 px-4 py-2 text-sm backdrop-blur"
    >
      <span className="text-foreground/85">
        {suggestion.reason === "preferred"
          ? copy.preferred(suggestion.site.name)
          : copy.available(suggestion.site.name)}
      </span>
      <a
        href={href}
        className="font-medium text-brand hover:underline"
        aria-disabled={saving}
        onClick={async (event) => {
          event.preventDefault();
          if (saving) return;
          setSaving(true);
          try {
            await savePreferredSite(suggestion.site.code);
            window.location.assign(href);
          } catch (error) {
            toast.error(error instanceof Error ? error.message : t("networkError"));
          } finally {
            setSaving(false);
          }
        }}
      >
        {copy.go(suggestion.site.name)}
      </a>
      {current ? (
        <button
          type="button"
          disabled={saving}
          className="text-muted-foreground hover:text-brand"
          onClick={async () => {
            setSaving(true);
            try {
              await savePreferredSite(current.code);
              setSuggestion(null);
              toast.success(t("languageSaved"));
            } catch (error) {
              toast.error(error instanceof Error ? error.message : t("networkError"));
            } finally {
              setSaving(false);
            }
          }}
        >
          {t("keepLanguage", { language: current.name })}
        </button>
      ) : null}
      <button
        type="button"
        onClick={dismiss}
        aria-label={copy.dismiss}
        className="ml-1 flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
      >
        <XIcon className="size-4" aria-hidden />
      </button>
    </div>
  );
}
